import React, { useState } from 'react';
import axios from 'axios';
import { User, MapPin, Calendar, Heart, Edit3, Save, X, Camera, Trash2, AlertTriangle, Phone } from 'lucide-react';
import { toast } from 'sonner';
import ProfilePhotos from '../components/ProfilePhotos';

const Profile = ({ user, onUserUpdate }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    full_name: user.full_name || '',
    age: user.age || '',
    location: user.location || '',
    mobile_number: user.mobile_number || '',
    bio: user.bio || '',
    mental_health_goals: user.mental_health_goals || [],
    openness_level: user.openness_level || 'open',
    interests: user.interests || [],
    relationship_status: user.relationship_status || 'single_looking',
    looking_for_description: user.looking_for_description || '',
    body_type: user.body_type || 'average',
    preferred_body_type: user.preferred_body_type || 'any',
    date_someone_with_children: user.date_someone_with_children || '',
    alcohol: user.alcohol || 'prefer_not_to_say',
    smoking: user.smoking || 'never',
    religious_beliefs: user.religious_beliefs || 'prefer_not_to_say'
  });

  const mentalHealthGoals = [
    'Building Confidence', 'Depression Support', 'Emotional Growth', 'Manage Anxiety', 
    'Mindfulness Practice', 'Self-Care', 'Stress Management', 'Therapy Journey'
  ];

  const interests = [
    'Art Therapy', 'Cooking', 'Events', 'Exercise', 'Fine Dining', 'Journaling', 
    'Meditation', 'Movies', 'Music', 'Nature Walks', 'Photography', 'Reading', 
    'Restaurants', 'Sports', 'Technology', 'Theatre', 'Volunteering', 'Yoga'
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: prev[name].includes(value)
        ? prev[name].filter(item => item !== value)
        : [...prev[name], value]
    }));
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put('/users/me', formData, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.data) {
        onUserUpdate(response.data);
        toast.success('Profile updated successfully!');
        setIsEditing(false);
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to update profile';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      full_name: user.full_name || '',
      age: user.age || '',
      location: user.location || '',
      mobile_number: user.mobile_number || '',
      bio: user.bio || '',
      mental_health_goals: user.mental_health_goals || [],
      openness_level: user.openness_level || 'open',
      interests: user.interests || [],
      relationship_status: user.relationship_status || 'single_looking',
      looking_for_description: user.looking_for_description || '',
      body_type: user.body_type || 'average',
      preferred_body_type: user.preferred_body_type || 'any',
      date_someone_with_children: user.date_someone_with_children || '',
      alcohol: user.alcohol || 'prefer_not_to_say',
      smoking: user.smoking || 'never',
      religious_beliefs: user.religious_beliefs || 'prefer_not_to_say'
    });
    setIsEditing(false);
  };

  return (
    <div className="min-h-screen pt-6 pb-12" data-testid="profile-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-emerald-800 mb-4">Your Profile</h1>
          <p className="text-lg text-emerald-600">
            Share your authentic self and wellness journey with the community.
          </p>
        </div>

        {/* Profile Card */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-8 border-2 border-emerald-100">
          {/* Profile Header */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
            <div className="flex items-center space-x-4 mb-4 md:mb-0">
              <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center border-4 border-emerald-200 overflow-hidden">
                {user.profile_image ? (
                  <img
                    src={`${process.env.REACT_APP_BACKEND_URL}${user.profile_image}`}
                    alt={user.username}
                    className="w-full h-full object-cover"
                    data-testid="profile-header-avatar"
                  />
                ) : (
                  <User className="w-10 h-10 text-emerald-600" />
                )}
              </div>
              <div>
                <h2 className="text-2xl font-bold text-emerald-800">{user.username}</h2>
                <div className="flex flex-col space-y-1 text-emerald-600 mt-2">
                  <div className="flex items-center space-x-1">
                    <Calendar className="w-4 h-4" />
                    <span>{user.age} years old</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <MapPin className="w-4 h-4" />
                    <span>{user.location}</span>
                  </div>
                </div>
                <p className="text-emerald-700">@{user.username}</p>
              </div>
            </div>

            {/* Edit Button */}
            <div className="flex space-x-2">
              {isEditing ? (
                <>
                  <button
                    onClick={handleCancel}
                    className="btn-secondary flex items-center space-x-2"
                  >
                    <X className="w-4 h-4" />
                    <span>Cancel</span>
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={loading}
                    className="btn-primary flex items-center space-x-2 disabled:opacity-50"
                  >
                    {loading ? (
                      <div className="spinner border-white"></div>
                    ) : (
                      <Save className="w-4 h-4" />
                    )}
                    <span>{loading ? 'Saving...' : 'Save Changes'}</span>
                  </button>
                </>
              ) : (
                <button
                  onClick={() => setIsEditing(true)}
                  className="btn-primary flex items-center space-x-2"
                  data-testid="edit-profile-btn"
                >
                  <Edit3 className="w-4 h-4" />
                  <span>Edit Profile</span>
                </button>
              )}
            </div>
          </div>

          {/* My Photos */}
          <ProfilePhotos onUserUpdate={onUserUpdate} />

          {/* Bio Section */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-emerald-800 mb-3 flex items-center space-x-2">
              <Heart className="w-5 h-5" />
              <span>About Me</span>
            </h3>
            {isEditing ? (
              <div>
                <textarea
                  name="bio"
                  value={formData.bio}
                  onChange={handleInputChange}
                  className="form-input min-h-[120px] resize-none w-full"
                  placeholder="Tell others about yourself, your journey, and what you're looking for..."
                />
                <p className="text-sm text-emerald-600 mt-2">
                  {formData.bio.length}/1000 characters
                </p>
              </div>
            ) : (
              <div className="bg-emerald-50 rounded-lg p-4">
                <p className="text-emerald-700 leading-relaxed">
                  {user.bio || 'No bio added yet. Click "Edit Profile" to add information about yourself.'}
                </p>
              </div>
            )}
          </div>

          {/* Basic Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-emerald-700 mb-2">Full Name</label>
              {isEditing ? (
                <input
                  type="text"
                  name="full_name"
                  value={formData.full_name}
                  onChange={handleInputChange}
                  className="form-input w-full"
                />
              ) : (
                <div className="bg-emerald-50 rounded-lg p-3">
                  <p className="text-emerald-700 font-medium">{user.full_name || 'Not specified'}</p>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-emerald-700 mb-2">Age</label>
              {isEditing ? (
                <input
                  type="number"
                  name="age"
                  value={formData.age}
                  onChange={handleInputChange}
                  className="form-input w-full"
                  min="18"
                />
              ) : (
                <div className="bg-emerald-50 rounded-lg p-3">
                  <p className="text-emerald-700 font-medium">{user.age || 'Not specified'}</p>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-emerald-700 mb-2">Location</label>
              {isEditing ? (
                <input
                  type="text"
                  name="location"
                  value={formData.location}
                  onChange={handleInputChange}
                  className="form-input w-full"
                  placeholder="City, Country"
                />
              ) : (
                <div className="bg-emerald-50 rounded-lg p-3">
                  <p className="text-emerald-700 font-medium">{user.location || 'Not specified'}</p>
                </div>
              )}
            </div>
          </div>

          {/* Relationship Status */}
          <div className="mt-8">
            <h3 className="text-lg font-semibold text-emerald-800 mb-3">Relationship Status</h3>
            {isEditing ? (
              <select
                name="relationship_status"
                value={formData.relationship_status}
                onChange={handleInputChange}
                className="form-input w-full"
              >
                <option value="single_looking">Single</option>
                <option value="separated">Separated</option>
                <option value="divorced">Divorced</option>
                <option value="widowed">Widowed</option>
                <option value="prefer_not_to_say">Prefer not to say</option>
              </select>
            ) : (
              <div className="bg-emerald-50 rounded-lg p-3">
                <p className="text-emerald-700 font-medium">
                  {(() => {
                    const statusMap = {
                      'single_looking': 'Single',
                      'separated': 'Separated',
                      'divorced': 'Divorced',
                      'widowed': 'Widowed',
                      'prefer_not_to_say': 'Prefer not to say'
                    };
                    return user.relationship_status ? statusMap[user.relationship_status] || 'Single' : 'Single';
                  })()}
                </p>
              </div>
            )}
          </div>

          {/* What am I looking for */}
          <div className="mt-8">
            <h3 className="text-lg font-semibold text-emerald-800 mb-3">What am I looking for</h3>
            {isEditing ? (
              <div>
                <textarea
                  name="looking_for_description"
                  value={formData.looking_for_description}
                  onChange={handleInputChange}
                  className="form-input min-h-[100px] resize-none w-full"
                  placeholder="Describe what kind of connection or relationship you're seeking..."
                />
                <p className="text-sm text-emerald-600 mt-2">
                  {formData.looking_for_description.length}/750 characters
                </p>
              </div>
            ) : (
              <div className="bg-emerald-50 rounded-lg p-3">
                <p className="text-emerald-700 leading-relaxed">
                  {user.looking_for_description || 'Not specified'}
                </p>
              </div>
            )}
          </div>

          {/* Religious Beliefs */}
          <div className="mt-8">
            <h3 className="text-lg font-semibold text-emerald-800 mb-3">Religious Beliefs</h3>
            {isEditing ? (
              <select
                name="religious_beliefs"
                value={formData.religious_beliefs}
                onChange={handleInputChange}
                className="form-input w-full"
              >
                <option value="prefer_not_to_say">Prefer not to say</option>
                <option value="christian">Christian</option>
                <option value="muslim">Muslim</option>
                <option value="jewish">Jewish</option>
                <option value="hindu">Hindu</option>
                <option value="buddhist">Buddhist</option>
                <option value="atheist">Atheist</option>
                <option value="agnostic">Agnostic</option>
                <option value="spiritual">Spiritual (non-religious)</option>
                <option value="other">Other</option>
              </select>
            ) : (
              <div className="bg-emerald-50 rounded-lg p-3">
                <p className="text-emerald-700 font-medium">
                  {(() => {
                    const religionMap = {
                      'christian': 'Christian',
                      'muslim': 'Muslim',
                      'jewish': 'Jewish',
                      'hindu': 'Hindu',
                      'buddhist': 'Buddhist',
                      'atheist': 'Atheist',
                      'agnostic': 'Agnostic',
                      'spiritual': 'Spiritual (non-religious)',
                      'other': 'Other',
                      'prefer_not_to_say': 'Prefer not to say'
                    };
                    return user.religious_beliefs ? religionMap[user.religious_beliefs] || 'Not specified' : 'Not specified';
                  })()}
                </p>
              </div>
            )}
          </div>

          {/* Interests & Hobbies */}
          <div className="mt-8">
            <h3 className="text-lg font-semibold text-emerald-800 mb-3">Interests & Hobbies</h3>
            {isEditing ? (
              <div className="grid grid-cols-2 gap-4">
                {interests.map(interest => (
                  <label key={interest} className="flex items-center space-x-3 cursor-pointer p-2 hover:bg-emerald-50 rounded">
                    <input
                      type="checkbox"
                      className="rounded border-emerald-300 text-emerald-600 focus:ring-emerald-500 flex-shrink-0"
                      checked={formData.interests.includes(interest)}
                      onChange={() => handleCheckboxChange('interests', interest)}
                    />
                    <span className="text-sm text-emerald-700 leading-tight">{interest}</span>
                  </label>
                ))}
              </div>
            ) : (
              <div className="bg-emerald-50 rounded-lg p-3">
                {user.interests && user.interests.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {user.interests.map(interest => (
                      <span key={interest} className="px-3 py-1 bg-emerald-200 text-emerald-700 rounded-full text-sm">
                        {interest}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-emerald-700">Not specified</p>
                )}
              </div>
            )}
          </div>

          {/* Mental Health Goals */}
          <div className="mt-8">
            <h3 className="text-lg font-semibold text-emerald-800 mb-3">Mental Health Goals</h3>
            {isEditing ? (
              <div className="grid grid-cols-2 gap-4">
                {mentalHealthGoals.map(goal => (
                  <label key={goal} className="flex items-center space-x-3 cursor-pointer p-2 hover:bg-emerald-50 rounded">
                    <input
                      type="checkbox"
                      className="rounded border-emerald-300 text-emerald-600 focus:ring-emerald-500 flex-shrink-0"
                      checked={formData.mental_health_goals.includes(goal)}
                      onChange={() => handleCheckboxChange('mental_health_goals', goal)}
                    />
                    <span className="text-sm text-emerald-700 leading-tight">{goal}</span>
                  </label>
                ))}
              </div>
            ) : (
              <div className="bg-emerald-50 rounded-lg p-3">
                {user.mental_health_goals && user.mental_health_goals.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {user.mental_health_goals.map(goal => (
                      <span key={goal} className="px-3 py-1 bg-emerald-200 text-emerald-700 rounded-full text-sm">
                        {goal}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-emerald-700">Not specified</p>
                )}
              </div>
            )}
          </div>

          {/* Lifestyle Information */}
          <div className="mt-8">
            <h3 className="text-lg font-semibold text-emerald-800 mb-3">Lifestyle Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-emerald-700 mb-2">My Body Type</label>
                {isEditing ? (
                  <select
                    name="body_type"
                    value={formData.body_type}
                    onChange={handleInputChange}
                    className="form-input w-full"
                  >
                    <option value="average">Average</option>
                    <option value="slim">Slim</option>
                    <option value="athletic">Athletic</option>
                    <option value="curvy">Curvy</option>
                    <option value="plus_size">Plus Size</option>
                    <option value="muscular">Muscular</option>
                    <option value="prefer_not_to_say">Prefer not to say</option>
                  </select>
                ) : (
                  <div className="bg-emerald-50 rounded-lg p-3">
                    <p className="text-emerald-700 font-medium">
                      {(() => {
                        const bodyTypeMap = {
                          'slim': 'Slim',
                          'average': 'Average',
                          'athletic': 'Athletic',
                          'curvy': 'Curvy',
                          'plus_size': 'Plus Size',
                          'muscular': 'Muscular',
                          'prefer_not_to_say': 'Prefer not to say'
                        };
                        return user.body_type ? bodyTypeMap[user.body_type] || 'Not specified' : 'Not specified';
                      })()}
                    </p>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-emerald-700 mb-2">Body Type I'm Looking For</label>
                {isEditing ? (
                  <select
                    name="preferred_body_type"
                    value={formData.preferred_body_type}
                    onChange={handleInputChange}
                    className="form-input w-full"
                  >
                    <option value="any">Any</option>
                    <option value="slim">Slim</option>
                    <option value="average">Average</option>
                    <option value="athletic">Athletic</option>
                    <option value="curvy">Curvy</option>
                    <option value="plus_size">Plus Size</option>
                    <option value="muscular">Muscular</option>
                    <option value="prefer_not_to_say">Prefer not to say</option>
                  </select>
                ) : (
                  <div className="bg-emerald-50 rounded-lg p-3">
                    <p className="text-emerald-700 font-medium">
                      {(() => {
                        const bodyTypeMap = {
                          'slim': 'Slim',
                          'average': 'Average',
                          'athletic': 'Athletic',
                          'curvy': 'Curvy',
                          'plus_size': 'Plus Size',
                          'muscular': 'Muscular',
                          'any': 'Any',
                          'prefer_not_to_say': 'Prefer not to say'
                        };
                        return user.preferred_body_type ? bodyTypeMap[user.preferred_body_type] || 'Not specified' : 'Not specified';
                      })()}
                    </p>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-emerald-700 mb-2">Date Someone With Children?</label>
                {isEditing ? (
                  <select
                    name="date_someone_with_children"
                    value={formData.date_someone_with_children}
                    onChange={handleInputChange}
                    className="form-input w-full"
                    data-testid="profile-children-preference-select"
                  >
                    <option value="">Not specified</option>
                    <option value="yes">Yes, I'm open to it</option>
                    <option value="no">No, I prefer not to</option>
                    <option value="maybe">Maybe, depends on the situation</option>
                    <option value="no_preference">No preference</option>
                    <option value="prefer_not_to_say">Prefer not to say</option>
                  </select>
                ) : (
                  <div className="bg-emerald-50 rounded-lg p-3">
                    <p className="text-emerald-700 font-medium">
                      {(() => {
                        const childrenMap = {
                          'yes': "Yes, I'm open to it",
                          'no': 'No, I prefer not to',
                          'maybe': 'Maybe, depends on the situation',
                          'no_preference': 'No preference',
                          'prefer_not_to_say': 'Prefer not to say'
                        };
                        return user.date_someone_with_children ? childrenMap[user.date_someone_with_children] || 'Not specified' : 'Not specified';
                      })()}
                    </p>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-emerald-700 mb-2">Alcohol</label>
                {isEditing ? (
                  <select
                    name="alcohol"
                    value={formData.alcohol}
                    onChange={handleInputChange}
                    className="form-input w-full"
                  >
                    <option value="prefer_not_to_say">Prefer not to say</option>
                    <option value="never">Never</option>
                    <option value="rarely">Rarely</option>
                    <option value="socially">Socially</option>
                    <option value="regularly">Regularly</option>
                  </select>
                ) : (
                  <div className="bg-emerald-50 rounded-lg p-3">
                    <p className="text-emerald-700 font-medium">
                      {(() => {
                        const alcoholMap = {
                          'never': 'Never',
                          'rarely': 'Rarely',
                          'socially': 'Socially',
                          'regularly': 'Regularly',
                          'prefer_not_to_say': 'Prefer not to say'
                        };
                        return user.alcohol ? alcoholMap[user.alcohol] || 'Not specified' : 'Not specified';
                      })()}
                    </p>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-emerald-700 mb-2">Smoking</label>
                {isEditing ? (
                  <select
                    name="smoking"
                    value={formData.smoking}
                    onChange={handleInputChange}
                    className="form-input w-full"
                  >
                    <option value="never">Never</option>
                    <option value="occasionally">Occasionally</option>
                    <option value="regularly">Regularly</option>
                    <option value="trying_to_quit">Trying to quit</option>
                    <option value="prefer_not_to_say">Prefer not to say</option>
                  </select>
                ) : (
                  <div className="bg-emerald-50 rounded-lg p-3">
                    <p className="text-emerald-700 font-medium">
                      {(() => {
                        const smokingMap = {
                          'never': 'Never',
                          'occasionally': 'Occasionally',
                          'regularly': 'Regularly',
                          'trying_to_quit': 'Trying to quit',
                          'prefer_not_to_say': 'Prefer not to say'
                        };
                        return user.smoking ? smokingMap[user.smoking] || 'Not specified' : 'Not specified';
                      })()}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Communication Style */}
          <div className="mt-8">
            <h3 className="text-lg font-semibold text-emerald-800 mb-3">Communication Style</h3>
            {isEditing ? (
              <select
                name="openness_level"
                value={formData.openness_level}
                onChange={handleInputChange}
                className="form-input w-full"
              >
                <option value="open">Open & Sharing</option>
                <option value="private">Private & Reserved</option>
                <option value="very_open">Very Open & Transparent</option>
              </select>
            ) : (
              <div className="bg-emerald-50 rounded-lg p-3">
                <p className="text-emerald-700 font-medium">
                  {(() => {
                    const opennessMap = {
                      'private': 'Private & Reserved',
                      'open': 'Open & Sharing',
                      'very_open': 'Very Open & Transparent'
                    };
                    return user.openness_level ? opennessMap[user.openness_level] || 'Not specified' : 'Not specified';
                  })()}
                </p>
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
};

export default Profile;